import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {EChartsOption} from 'echarts';
import {ScoreDevelopmentChipVm, ScoreDevelopmentVm} from '../../models/team-dynamic.vm';

interface ScoreDevelopmentChipView {
  chip: ScoreDevelopmentChipVm;
  active: boolean;
  buttonStyle: { [key: string]: string };
  iconStyle: { [key: string]: string };
}

@Component({
  selector: 'app-score-development',
  templateUrl: './score-development.component.html',
  styleUrls: ['./score-development.component.scss']
})
export class ScoreDevelopmentComponent implements OnChanges {
  @Input() scores: ScoreDevelopmentVm = {labels: [], values: []};
  @Input() chips: ScoreDevelopmentChipVm[] = [];
  @Input() activeChipUuid: string | null = null;
  @Input() loading: boolean = false;
  @Input() showChips: boolean = true;
  @Input() titleKey: string = 'td_score_development';

  @Output() chipSelected = new EventEmitter<string | null>();

  hasData: boolean = false;
  showChart: boolean = true;
  activeChipTitle: string = '';
  chipViews: ScoreDevelopmentChipView[] = [];

  private activeColor: string = '#F2762E';

  chartOption: EChartsOption = {
    grid: {
      left: 4,
      right: 16,
      top: 34,
      bottom: 4,
      containLabel: true
    },
    xAxis: {
      type: 'category',
      boundaryGap: true,
      axisLabel: {
        color: '#8B93AB',
        fontWeight: 'bold',
        fontSize: 10
      },
      axisLine: {
        show: false
      },
      axisTick: {
        show: false
      },
      data: []
    },
    yAxis: {
      type: 'value',
      min: 0,
      max: 100,
      interval: 25,
      axisLabel: {
        color: '#8B93AB',
        fontWeight: 'bold',
        fontSize: 10,
        formatter: (value: number) => (value % 50 === 0 ? '' + value : '')
      },
      axisLine: {
        show: false
      },
      axisTick: {
        show: false
      },
      splitLine: {
        lineStyle: {
          color: '#E9EDF6'
        }
      }
    },
    tooltip: {
      trigger: 'axis',
      padding: [6, 10],
      textStyle: {
        fontSize: 11,
        color: '#16223F'
      },
      formatter: (params: any) => {
        const point = Array.isArray(params) ? params[0] : params;
        return point.name + '<br/>'
          + '<span style="display:inline-block;width:.8rem;height:.8rem;border-radius:50%;'
          + 'margin-right:.6rem;background-color:' + this.activeColor + '"></span>'
          + this.activeChipTitle + ': <b>' + point.value + '%</b>';
      }
    },
    series: [
      {
        name: '',
        type: 'line',
        data: [],
        smooth: false,
        symbol: 'circle',
        symbolSize: 9,
        label: {
          show: true,
          position: 'top',
          distance: 8,
          color: '#16223F',
          fontWeight: 'bold',
          fontSize: 11,
          formatter: '{c}%'
        },
        tooltip: {
          trigger: 'axis'
        }
      }
    ]
  };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['chips'] || changes['activeChipUuid']) {
      this.buildChipViews();
    }
    if (changes['scores'] || changes['activeChipUuid'] || changes['chips']) {
      this.fillChart();
    }
  }

  selectChip(chip: ScoreDevelopmentChipVm) {
    this.chipSelected.emit(chip.uuid);
  }

  trackByUuid(index: number, view: ScoreDevelopmentChipView) {
    return view.chip.uuid ?? index;
  }

  private buildChipViews() {
    this.chipViews = this.chips.map(chip => {
      const active = chip.uuid === this.activeChipUuid;
      const buttonStyle: { [key: string]: string } = active
        ? {'background-color': chip.color, 'border-color': chip.color}
        : {};
      return {
        chip,
        active,
        buttonStyle,
        iconStyle: {
          '-webkit-mask-image': 'url(' + chip.icon + ')',
          'mask-image': 'url(' + chip.icon + ')',
          'background-color': active ? '#FFFFFF' : chip.color
        }
      };
    });
  }

  private fillChart() {
    const labels = this.scores?.labels ?? [];
    const values = this.scores?.values ?? [];
    const activeChip = this.chips.find(chip => chip.uuid === this.activeChipUuid);
    const color = activeChip?.color ?? '#F2762E';

    this.hasData = values.length > 0;
    this.activeChipTitle = activeChip?.title ?? '';
    this.activeColor = color;

    // @ts-ignore
    const series = this.chartOption.series[0];
    series.name = this.activeChipTitle;
    series.data = [];
    series.color = color;
    series.lineStyle = {color, width: 2};
    series.itemStyle = {color: '#FFFFFF', borderColor: color, borderWidth: 2};
    // @ts-ignore
    this.chartOption.xAxis.data = [];

    labels.forEach((label: string, index: number) => {
      // @ts-ignore
      this.chartOption.xAxis.data.push(label);
      series.data.push({value: values[index], name: label});
    });

    this.showChart = !this.showChart;
  }
}
